import { useIntl } from 'react-intl'
import Group from 'components/utils/Group'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import LoyaltyRewardsIcon from 'LuxKit/Icons/loyalty/LoyaltyRewardsIcon'
import { rem } from 'polished'
import styled from 'styled-components'
import { useCurrencyFormatter } from 'hooks/useNumberFormatters'

const DEFAULT_COLUMN_WIDTH = 168
const GUESTS_COLUMN_WIDTH = DEFAULT_COLUMN_WIDTH / 2

const Root = styled(VerticalSpacer)`
  width: 100%;
  min-width: 0;
`

const TableScroller = styled.div`
  box-sizing: border-box;
  width: 100%;
  padding-inline: ${rem(12)};
  overflow-x: auto;
`

const TableCentered = styled.div`
  display: flex;
  justify-content: center;
  min-width: max-content;
`

const RewardsTable = styled.table`
  width: max-content;
  border-collapse: separate;
  border-spacing: 0;
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${props => props.theme.borderRadius.S};
  overflow: hidden;
`

const HeaderCell = styled.th`
  width: ${rem(DEFAULT_COLUMN_WIDTH)};
  padding: ${rem(12)} ${rem(8)};
  background: ${props => props.theme.palette.neutral.default.eight};
  border-bottom: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  color: ${props => props.theme.palette.neutral.default.one};

  &.highlight {
    background: ${props => props.theme.palette.highlight.primary.lightBackground};
    border-bottom: none;
    color: ${props => props.theme.palette.highlight.primary.darkBackground};
  }
`

const BodyCell = styled.td`
  width: ${rem(DEFAULT_COLUMN_WIDTH)};
  padding: ${rem(12)} ${rem(8)};
  vertical-align: middle;
  background: ${props => props.theme.palette.neutral.default.eight};
  border-bottom: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};

  &.highlight {
    background: ${props => props.theme.palette.highlight.primary.lightBackground};
    border-bottom: none;
  }
`

const TableRow = styled.tr`
  &:last-child ${BodyCell} {
    border-bottom: 0;
  }
`

const PackageCell = styled(BodyCell)`
  width: ${rem(DEFAULT_COLUMN_WIDTH)};
`

const GuestsCell = styled(BodyCell)`
  width: ${rem(GUESTS_COLUMN_WIDTH)};
  text-align: center;
`

const PriceCell = styled(BodyCell)`
  width: ${rem(DEFAULT_COLUMN_WIDTH)};
  text-align: center;
`

const PackageHeaderCell = styled(HeaderCell)`
  text-align: left;
`

const CenteredHeaderCell = styled(HeaderCell)`
  text-align: center;
`

const CenteredGuestsHeaderCell = styled(CenteredHeaderCell)`
  width: ${rem(GUESTS_COLUMN_WIDTH)};
`

function LoyaltyRewardsIntroTable() {
  const intl = useIntl()
  const formatAudCurrency = useCurrencyFormatter({ currency: 'AUD' })

  const packageColumnLabel = intl.formatMessage({
    id: 'LsogaAWZAMBbOjhV5hj0',
    defaultMessage: 'Maldives Package',
    description: 'Table header for the Maldives package column in the Société Rewards intro table',
  })
  const guestsColumnLabel = intl.formatMessage({
    id: '7qMp2gR_0UzJkQZMCEkV',
    defaultMessage: 'Guests',
    description: 'Table header for the guest count column in the Société Rewards intro table',
  })
  const rewardsColumnLabel = intl.formatMessage({
    id: 'oB6pz7BL-ozi_M3Ekmwq',
    defaultMessage: 'Société Rewards',
    description: 'Table header for the Société Rewards price column in the rewards intro table',
  })
  const payInFullColumnLabel = intl.formatMessage({
    id: 'W8AtvGB5S9eHbR3iQEU1',
    defaultMessage: 'Pay in full',
    description: 'Table header for the pay-in-full cash price column in the Société Rewards intro table',
  })
  const economyLabel = intl.formatMessage({
    id: '8_41AhuRcOgVxo92zxVp',
    defaultMessage: 'Economy',
    description: 'Economy fare row label in the Société Rewards intro table',
  })
  const economyPlusLabel = intl.formatMessage({
    id: 'AvrRazvSs4sZHx_KKwGG',
    defaultMessage: 'Economy Plus',
    description: 'Economy Plus fare row label in the Société Rewards intro table',
  })
  const businessLabel = intl.formatMessage({
    id: 'Xu-Dr-t5Q5bXYkCs7_hX',
    defaultMessage: 'Business',
    description: 'Business fare row label in the Société Rewards intro table',
  })
  const packageDescription = intl.formatMessage({
    id: 'Pv0TnChS4nAHsLGAamhE',
    defaultMessage: 'Return flights & 5 night stay',
    description: 'Package description shown under each fare class in the Société Rewards intro table',
  })
  const guestsLabel = intl.formatMessage({
    id: 'UdqKgoKVeNJhu5eIproe',
    defaultMessage: '2 adults',
    description: 'Guest count shown for each fare class in the Société Rewards intro table',
  })
  const disclaimer = intl.formatMessage({
    id: 'X_TApK9yTtm5NqHckJRU',
    defaultMessage: 'Hurry, limited seats! Points & prices are based on LuxPlus+ member prices and subject to change – book now to lock in this exclusive offer.',
    description: 'Disclaimer below the hardcoded Société Rewards intro comparison table on the rewards-seats-maldives landing page',
  })
  const economyRewardCashLabel = intl.formatMessage(
    {
      id: 'pLiAhtE0RMNLKop_xJ3P',
      defaultMessage: 'From {cashAmount} +',
      description: 'Société Rewards cash prefix for economy fare in the intro table',
    },
    { cashAmount: formatAudCurrency(1299, 'roundedDollar') },
  )
  const economyRewardPointsLabel = intl.formatMessage({
    id: 'zub85GrEb_m4qns33UbA',
    defaultMessage: '120,000 pts/person',
    description: 'Hardcoded Société Rewards points suffix for economy fare in the intro table',
  })
  const economyPayInFullLabel = intl.formatMessage(
    {
      id: 'L1BqK0OxgSBzn363-9Xb',
      defaultMessage: 'From {cashAmount}/person',
      description: 'Pay-in-full cash value for economy fare in the intro table',
    },
    { cashAmount: formatAudCurrency(2299, 'roundedDollar') },
  )
  const economyPlusRewardCashLabel = intl.formatMessage(
    {
      id: 'xTQYl0SqUfbhMpTIWjCr',
      defaultMessage: 'From {cashAmount} +',
      description: 'Société Rewards cash prefix for economy plus fare in the intro table',
    },
    { cashAmount: formatAudCurrency(1399, 'roundedDollar') },
  )
  const economyPlusRewardPointsLabel = intl.formatMessage({
    id: 'kEdbjka3mfoto78c4S3G',
    defaultMessage: '200,000 pts/person',
    description: 'Hardcoded Société Rewards points suffix for economy plus fare in the intro table',
  })
  const economyPlusPayInFullLabel = intl.formatMessage(
    {
      id: '-vO_u4hVFTkjWqtBMhYr',
      defaultMessage: 'From {cashAmount}/person',
      description: 'Pay-in-full cash value for economy plus fare in the intro table',
    },
    { cashAmount: formatAudCurrency(2799, 'roundedDollar') },
  )
  const businessRewardCashLabel = intl.formatMessage(
    {
      id: '8NNijhYQeDPLeOAYf977',
      defaultMessage: 'From {cashAmount} +',
      description: 'Société Rewards cash prefix for business fare in the intro table',
    },
    { cashAmount: formatAudCurrency(1799, 'roundedDollar') },
  )
  const businessRewardPointsLabel = intl.formatMessage({
    id: 'j-qamRolnT-qoa0kterk',
    defaultMessage: '500,000 pts/person',
    description: 'Hardcoded Société Rewards points suffix for business fare in the intro table',
  })
  const businessPayInFullLabel = intl.formatMessage(
    {
      id: 'hKR_5WaS8afAHWc7I74j',
      defaultMessage: 'From {cashAmount}/person',
      description: 'Pay-in-full cash value for business fare in the intro table',
    },
    { cashAmount: formatAudCurrency(6699, 'roundedDollar') },
  )

  const rows = [
    {
      fareClass: economyLabel,
      rewardCash: economyRewardCashLabel,
      rewardPoints: economyRewardPointsLabel,
      payInFull: economyPayInFullLabel,
    },
    {
      fareClass: economyPlusLabel,
      rewardCash: economyPlusRewardCashLabel,
      rewardPoints: economyPlusRewardPointsLabel,
      payInFull: economyPlusPayInFullLabel,
    },
    {
      fareClass: businessLabel,
      rewardCash: businessRewardCashLabel,
      rewardPoints: businessRewardPointsLabel,
      payInFull: businessPayInFullLabel,
    },
  ]

  return <Root gap={12}>
    <TableScroller>
      <TableCentered>
        <RewardsTable>
          <thead>
            <tr>
              <PackageHeaderCell>
                <BodyText variant="small" weight="bold">{packageColumnLabel}</BodyText>
              </PackageHeaderCell>
              <CenteredGuestsHeaderCell>
                <BodyText variant="small" weight="bold">{guestsColumnLabel}</BodyText>
              </CenteredGuestsHeaderCell>
              <CenteredHeaderCell className="highlight">
                <Group direction="horizontal" gap={4} verticalAlign="center" horizontalAlign="center">
                  <LoyaltyRewardsIcon size={16} colour="highlight-primary-dark" />
                  <BodyText variant="small" weight="bold">{rewardsColumnLabel}</BodyText>
                </Group>
              </CenteredHeaderCell>
              <CenteredHeaderCell>
                <BodyText variant="small" weight="bold">{payInFullColumnLabel}</BodyText>
              </CenteredHeaderCell>
            </tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <TableRow key={row.fareClass}>
                <PackageCell>
                  <BodyText variant="small" weight="bold">{row.fareClass}</BodyText>
                  <BodyText variant="small">{packageDescription}</BodyText>
                </PackageCell>
                <GuestsCell>
                  <BodyText variant="small">{guestsLabel}</BodyText>
                </GuestsCell>
                <PriceCell className="highlight">
                  <BodyText variant="small" weight="bold">{row.rewardCash}</BodyText>
                  <BodyText variant="small">{row.rewardPoints}</BodyText>
                </PriceCell>
                <PriceCell>
                  <BodyText variant="small" weight="bold">{row.payInFull}</BodyText>
                </PriceCell>
              </TableRow>
            ))}
          </tbody>
        </RewardsTable>
      </TableCentered>
    </TableScroller>
    <Caption variant="medium" align="center">{disclaimer}</Caption>
  </Root>
}

export default LoyaltyRewardsIntroTable
